import React from 'react';
import { useTranslation } from '@/i18n';

import { useRoleForm } from '@/hooks/authorization/useRoleForm';
import { formatToDdMmYyyy } from '@/utils';

import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { TextArea } from '@/components/ui/inputs/TextArea';


interface RoleFormPanelProps {
  roleId?: string;
  mode?: 'view' | 'edit';
  onSuccess?: () => void;
}

export const RoleFormPanel: React.FC<RoleFormPanelProps> = ({ roleId, mode = 'edit', onSuccess }) => {
  const { t: translate } = useTranslation();
  const isViewMode = mode === 'view';

  const { form, roleDetail, isReady, onSubmit } = useRoleForm({ roleId, onSuccess });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isSubmitted },
  } = form;

  const description = watch('description');

  return (
    <SidePanelFormLayout onSubmit={handleSubmit(onSubmit)}>
      <SidePanelFormLayout.Fields>
        {isViewMode && roleDetail && (
          <LabelInput
            label={translate('authorization:roles.fields.id')}
            value={roleDetail.id}
            disabled
          />
        )}

        <LabelInput
          label={translate('authorization:roles.fields.name')}
          supportingText={
            errors.name?.message && isSubmitted ? translate(errors.name.message) : undefined
          }
          color={errors.name && isSubmitted ? 'error' : 'default'}
          registration={register('name')}
          required
          disabled={isViewMode}
        />
        
        <TextArea
          label={translate('authorization:roles.fields.description')}
          supportingText={
            errors.description?.message && isSubmitted
              ? translate(errors.description.message)
              : undefined
          }
          color={errors.description && isSubmitted ? 'error' : 'default'}
          registration={register('description')}
          rows={4}
          maxLength={200}
          showCharacterCount
          disabled={isViewMode}
          value={description || ''}
        />

        {isViewMode && roleDetail && (
          <>
            <LabelInput
              label={translate('authorization:roles.fields.status')}
              value={roleDetail.status ?? '-'}
              disabled
            />

            <LabelInput
              label={translate('authorization:roles.fields.createdAt')}
              value={formatToDdMmYyyy(roleDetail.createdAt) || '-'}
              disabled
            />

            <LabelInput
              label={translate('authorization:roles.fields.createdBy')}
              value={roleDetail.createdBy || '-'}
              disabled
            />

            <LabelInput
              label={translate('authorization:roles.fields.modifiedAt')}
              value={formatToDdMmYyyy(roleDetail.modifiedAt) || '-'}
              disabled
            />

            <LabelInput
              label={translate('authorization:roles.fields.modifiedBy')}
              value={roleDetail.modifiedBy || '-'}
              disabled
            />
          </>
        )}
      </SidePanelFormLayout.Fields>

      {!isViewMode && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action={roleId ? 'edit' : 'create'}>
            <PrimaryButton type="submit" fullWidth isLoading={isSubmitting} disabled={!isReady || isSubmitting}>
              {roleId 
                ? translate('authorization:roles.actions.update') 
                : translate('authorization:roles.actions.create')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};
